import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';
import { login } from '../services/authService';
import { getAgencyById } from '../services/agenciesService';

const Login = ({ onAdminLogin, onCustomerLogin, onAgencyLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    // Validate form
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const email = formData.email.toLowerCase().trim();
    const password = formData.password;

    try {
      // API login
      const response = await login(email, password);
      
      // Handle successful login
      const user = response.user || response;
      const userRole = user.role || user.user_type || user.userType;
      
      // Set authentication state based on role
      if (userRole === 'admin' || userRole === 'Admin') {
        if (onAdminLogin) {
          onAdminLogin();
        }
        localStorage.setItem('isAuthenticated', 'true');
        if (formData.remember) {
          localStorage.setItem('rememberedEmail', email);
        }
        navigate('/dashboard');
      } else if (userRole === 'customer' || userRole === 'Customer') {
        if (onCustomerLogin) {
          onCustomerLogin();
        }
        localStorage.setItem('customerAuthenticated', 'true');
        if (formData.remember) {
          localStorage.setItem('rememberedEmail', email);
        }
        navigate('/truck-search');
      } else if (userRole === 'agency' || userRole === 'Agency') {
        // Check if user status is Active
        const userStatus = user.status || user.user_status || '';
        
        // Check if user is inactive
        if (userStatus && userStatus.toLowerCase() !== 'active') {
          setLoginError('Your account is inactive. Please contact administrator for assistance.');
          setLoading(false);
          return;
        }
        
        // Check agency status - try from response first, then fetch if needed
        let agencyStatus = user.agency?.status || user.agency_status || user.agencyStatus || '';
        const agencyId = user.agency_id || user.agencyId || user.agency?.id;
        
        // If agency status not in response but we have agency_id, fetch it
        if (!agencyStatus && agencyId) {
          try {
            const agencyResponse = await getAgencyById(agencyId);
            const agencyData = agencyResponse?.data || agencyResponse?.success?.data || agencyResponse;
            agencyStatus = agencyData?.status || agencyData?.agency_status || '';
          } catch (err) {
            console.error('Error fetching agency status:', err);
            // Continue with login if we can't fetch agency status
            // Backend should ideally handle this check
          }
        }
        
        // Check if agency is inactive
        if (agencyStatus && agencyStatus.toLowerCase() !== 'active') {
          setLoginError('Your transporter account is inactive. Please contact administrator for assistance.');
          setLoading(false);
          return;
        }
        
        if (onAgencyLogin) {
          onAgencyLogin();
        }
        localStorage.setItem('agencyAuthenticated', 'true');
        if (formData.remember) {
          localStorage.setItem('rememberedEmail', email);
        }
        navigate('/agency-dashboard');
      } else {
        // Unknown role - default to customer dashboard
        if (onCustomerLogin) {
          onCustomerLogin();
        }
        localStorage.setItem('customerAuthenticated', 'true');
        if (formData.remember) {
          localStorage.setItem('rememberedEmail', email);
        }
        navigate('/truck-search');
      }
    } catch (error) {
      // API login failed
      console.error('API login error:', error);
      setLoginError(error.message || 'Invalid email or password. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load remembered email on mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        remember: true
      }));
    }
  }, []);


  // Get time-based greeting
  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Left Section - Welcome Page */}
        <div className="welcome-section">
          <div className="welcome-background">
            <div className="gradient-shape shape-1"></div>
            <div className="gradient-shape shape-2"></div>
            <div className="gradient-shape shape-3"></div>
            <div className="gradient-circle circle-1"></div>
            <div className="gradient-circle circle-2"></div>
            <div className="gradient-circle circle-3"></div>
          </div>
          <div className="welcome-content">
            <h1 className="welcome-title">Welcome Page</h1>
            <p className="welcome-subtitle">Sign In To Your Account</p>
          </div>
          <div className="welcome-footer">
            <div className="wave-bottom">
              <p className="footer-text">WWW.NETTAA.COM</p>
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="login-section">
          <div className="login-form-container">
            <h2 className="greeting">Hello !</h2>
            <h3 className="time-greeting">{getTimeGreeting()}</h3>
            <h4 className="form-title">Login Your Account</h4>
            
            {loginError && (
              <div className="error-message" role="alert">
                {loginError}
              </div>
            )}

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <span className="field-error">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className={`form-input ${errors.password ? 'error' : ''}`}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className="field-error">{errors.password}</span>
                )}
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="remember"
                    checked={formData.remember}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">Remember</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'LOGGING IN...' : 'LOGIN'}
              </button>
            </form>

            <div className="create-account">
              <Link to="/signup" className="create-link">Create Account</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

